import { mt } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { Download } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type {
  ReportOrganization,
  ReportScope
} from 'v5/core/reporting/reportScope';
import { Button } from 'v5/design-system/Button';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'v5/design-system/Form';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { getUserFacingErrorMessage } from 'v5/platform/lang/getUserFacingErrorMessage';
import { WithServices } from 'v5/platform/ts/services';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { ReportScopeSelector } from './ReportScopeSelector';

export type DownloadReportDialogContentProps = WithServices<
  [WithEnterpriseService]
> & {
  open: boolean;
  organizationId: string;
  organization: ReportOrganization;
  templateName: string;
  onSubmit: (scope: ReportScope) => Promise<void>;
};

type DownloadReportFormData = { scope: ReportScope | null };

function scopeDisplay(
  scope: ReportScope,
  organization: ReportOrganization,
  wholeOrganizationLabel: string
) {
  switch (scope.type) {
    case 'workspace':
      return {
        name: scope.workspace.name,
        iconUrl: scope.workspace.iconUrl,
        label: scope.workspace.name
      };
    case 'organization':
      return { ...organization, label: wholeOrganizationLabel };
  }
}

function ScopeTriggerContent({
  name,
  iconUrl,
  label
}: ReturnType<typeof scopeDisplay>) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <EnterpriseWorkspaceIcon name={name} iconUrl={iconUrl} />
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * Downloads a report template from the organisation's view: the user picks the workspace whose
 * records the file holds, or the whole organisation.
 */
export function DownloadReportDialogContent({
  open,
  organizationId,
  organization,
  templateName,
  onSubmit,
  services
}: DownloadReportDialogContentProps) {
  const { t } = useTranslation('reporting', {
    keyPrefix: 'DownloadReportDialog'
  });
  const mutation = promiseState.useMutation(onSubmit);
  const form = useForm<DownloadReportFormData>({
    defaultValues: { scope: null }
  });
  const scope = form.watch('scope');

  return (
    <Form {...form}>
      <FormDialogContent
        open={open}
        title={t('title')}
        onSubmit={form.handleSubmit(data => {
          if (data.scope === null || mt.isInProgress(mutation.state)) return;
          mutation.track(data.scope).catch(() => {});
        })}
        mutation={mutation.state}
        saveText={t('download')}
        footerContent={
          <DialogErrorFooter
            compact
            mutation={mutation.state}
            renderError={error =>
              getUserFacingErrorMessage(error) || t('downloadFailed')
            }
          />
        }
        saveButtonSlot={
          <AsyncButton
            type="submit"
            mutation={mutation.state}
            variant="primary"
            className="shrink-0 self-center"
            visuallyDisabled={scope === null}
            leftIcon={<Download className="size-4" />}
          >
            {t('download')}
          </AsyncButton>
        }
        renderHeader={title => (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{templateName}</DialogDescription>
          </DialogHeader>
        )}
      >
        <FormField
          control={form.control}
          name="scope"
          rules={{ required: true }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fieldScope')}</FormLabel>
              <FormControl>
                <ReportScopeSelector
                  services={services}
                  organizationId={organizationId}
                  organization={organization}
                  organizationLabel={t('wholeOrganization')}
                  onScopeSelect={field.onChange}
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full justify-start font-normal"
                  >
                    {field.value === null ? (
                      <span className="truncate text-fg-muted">
                        {t('selectScope')}
                      </span>
                    ) : (
                      <ScopeTriggerContent
                        {...scopeDisplay(
                          field.value,
                          organization,
                          t('wholeOrganization')
                        )}
                      />
                    )}
                  </Button>
                </ReportScopeSelector>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </FormDialogContent>
    </Form>
  );
}
